import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { ArrowLeft, DollarSign, RefreshCw, Search, Receipt, CreditCard, CircleDollarSign,
  CheckCircle2, Clock, AlertTriangle, XCircle, Loader2, ExternalLink, Hash, User, Info,
} from 'lucide-react';
import { formatDateMMDDYYYY } from '../../utils/timeFormat';
import StatusLegend from '../../components/StatusLegend';

/**
 * AdminPaymentFlow
 *
 * One-screen money trail per invoice: amount → Stripe → platform fee →
 * talent payout status. Backed by GET /api/admin/payments/invoice-flow.
 *
 * Status chips are color-coded to be instantly scannable:
 *   Invoice : unpaid (amber) | paid (emerald)
 *   Payout  : none (gray) | pending (amber) | processing (sky) |
 *             released (emerald) | failed (rose) | mixed (purple)
 */

const fmt$ = (n) =>
  n == null
    ? '—'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return formatDateMMDDYYYY(new Date(iso)); } catch { return iso; }
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

// ─────────────────────────────────────────────────────────────────────
// Status chips — color rules below match the user's "easy-to-scan" spec
// ─────────────────────────────────────────────────────────────────────

const InvoiceChip = ({ status }) => {
  const map = {
    paid:               { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: CheckCircle2, label: 'Paid' },
    unpaid:             { cls: 'bg-amber-100 text-amber-800 border-amber-300',         Icon: Clock,        label: 'Unpaid' },
    pending:            { cls: 'bg-amber-100 text-amber-800 border-amber-300',         Icon: Clock,        label: 'Pending' },
    pending_approval:   { cls: 'bg-purple-100 text-purple-800 border-purple-300',       Icon: Clock,        label: 'Pending Approval' },
    overdue:            { cls: 'bg-rose-100 text-rose-800 border-rose-300',             Icon: AlertTriangle, label: 'Overdue' },
    partial:            { cls: 'bg-sky-100 text-sky-800 border-sky-300',                Icon: Clock,        label: 'Partial' },
    cancelled:          { cls: 'bg-gray-100 text-gray-700 border-gray-300',             Icon: XCircle,      label: 'Cancelled' },
    refunded:           { cls: 'bg-gray-100 text-gray-700 border-gray-300',             Icon: XCircle,      label: 'Refunded' },
  };
  const cfg = map[status] || { cls: 'bg-gray-100 text-gray-700 border-gray-300', Icon: Hash, label: status || '—' };
  const I = cfg.Icon;
  return (
    <Badge className={`${cfg.cls} border font-medium`} data-testid={`invoice-status-${status}`}>
      <I className="w-3 h-3 mr-1" /> {cfg.label}
    </Badge>
  );
};

const PayoutChip = ({ status }) => {
  const map = {
    none:        { cls: 'bg-gray-100 text-gray-700 border-gray-300',         Icon: Hash,         label: 'No payout yet' },
    pending:     { cls: 'bg-amber-100 text-amber-800 border-amber-300',       Icon: Clock,        label: 'Pending' },
    processing:  { cls: 'bg-sky-100 text-sky-800 border-sky-300',             Icon: Loader2,      label: 'Processing' },
    released:    { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: CheckCircle2, label: 'Released' },
    completed:   { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: CheckCircle2, label: 'Released' },
    succeeded:   { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: CheckCircle2, label: 'Released' },
    failed:      { cls: 'bg-rose-100 text-rose-800 border-rose-300',          Icon: XCircle,      label: 'Failed' },
    mixed:       { cls: 'bg-purple-100 text-purple-800 border-purple-300',     Icon: AlertTriangle, label: 'Partial' },
  };
  const cfg = map[status] || map.none;
  const I = cfg.Icon;
  const spin = (status === 'processing') ? 'animate-spin' : '';
  return (
    <Badge className={`${cfg.cls} border font-medium`} data-testid={`payout-status-${status}`}>
      <I className={`w-3 h-3 mr-1 ${spin}`} /> {cfg.label}
    </Badge>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Iter-133u (Phase D) — Post-event survey gate badge + override dialog
// ─────────────────────────────────────────────────────────────────────

const SurveyGateBadge = ({ gate }) => {
  if (!gate) return null;
  // Accept both payload shapes:
  //   • payout row → `survey_gate: { status: ... }`
  //   • shift roster row → `{ survey_status: ... }`  (flat)
  const status = gate.status || gate.survey_status;
  const map = {
    not_required: {
      cls: 'bg-gray-100 text-gray-700 border-gray-300',
      icon: <CheckCircle2 className="w-3 h-3 mr-1 text-gray-400" />,
      label: 'Survey n/a',
      title: 'Survey not required for this role.',
    },
    completed: {
      cls: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      icon: <CheckCircle2 className="w-3 h-3 mr-1" />,
      label: 'Survey ✅',
      title: 'Post-event survey submitted — payout gate cleared.',
    },
    missing: {
      cls: 'bg-rose-100 text-rose-800 border-rose-300',
      icon: <XCircle className="w-3 h-3 mr-1" />,
      label: 'Survey ❌',
      title: 'Post-event survey is REQUIRED and NOT submitted. Payout is blocked until the talent submits — or an admin applies an override.',
    },
    override_applied: {
      cls: 'bg-amber-100 text-amber-800 border-amber-300',
      icon: <AlertTriangle className="w-3 h-3 mr-1" />,
      label: 'Override ⚠',
      title: `Survey requirement overridden by admin.${gate.override_reason ? ' Reason: ' + gate.override_reason : ''}`,
    },
  };
  const cfg = map[status] || map.missing;
  return (
    <Badge
      className={`${cfg.cls} border font-medium`}
      title={cfg.title}
      data-testid={`survey-gate-${status || 'unknown'}`}
    >
      {cfg.icon} {cfg.label}
    </Badge>
  );
};

const SurveyOverrideDialog = ({ open, onClose, bookingId, talentName, onApplied }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setReason(''); }, [open]);

  const submit = async () => {
    const r = reason.trim();
    if (r.length < 5) {
      toast.error('Reason must be at least 5 characters.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/payments/bookings/${bookingId}/survey-override`, { reason: r });
      toast.success('Survey requirement overridden. Payout can now proceed.');
      onApplied?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply override');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="survey-override-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Override survey requirement
          </DialogTitle>
          <DialogDescription>
            Bypass the post-event survey gate for <strong>{talentName}</strong> so
            their payout can proceed. This action is logged with your admin id + the
            reason below.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="text-sm font-medium text-gray-700">
            Reason (required, min 5 chars)
          </label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Client waived requirement · client OOO · survey data captured offline"
            maxLength={500}
            data-testid="survey-override-reason-input"
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="survey-override-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={busy || reason.trim().length < 5}
              data-testid="survey-override-submit-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Apply Override
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Top-of-page totals row
// ─────────────────────────────────────────────────────────────────────

const TotalsCard = ({ label, value, sub, color = 'cyan', icon: Icon }) => (
  <Card className="border-l-4" style={{ borderLeftColor: color === 'emerald' ? '#10b981' : color === 'amber' ? '#f59e0b' : color === 'rose' ? '#f43f5e' : color === 'purple' ? '#a855f7' : '#06b6d4' }}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
      </div>
      <div className="text-2xl font-bold mt-1 text-gray-900">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </CardContent>
  </Card>
);

// ─────────────────────────────────────────────────────────────────────
// Drill-down modal
// ─────────────────────────────────────────────────────────────────────

const PayoutSurveyOverrideInline = ({ bookingId, talentName, onApplied }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="flex items-center gap-2 pt-1">
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
          onClick={() => setOpen(true)}
          data-testid={`survey-override-open-${bookingId}`}
        >
          <AlertTriangle className="w-3 h-3 mr-1" /> Override survey
        </Button>
        <span className="text-[11px] text-rose-700">
          Payout blocked — survey not submitted.
        </span>
      </div>
      <SurveyOverrideDialog
        open={open}
        onClose={() => setOpen(false)}
        bookingId={bookingId}
        talentName={talentName}
        onApplied={onApplied}
      />
    </>
  );
};

const ShiftSurveyRosterSection = ({ bookings, onRefresh }) => {
  const [overrideTarget, setOverrideTarget] = useState(null);
  const [clearing, setClearing] = useState(null);

  if (!bookings || bookings.length === 0) return null;

  const clearOverride = async (bookingId) => {
    if (!window.confirm('Remove this override and re-arm the survey gate?')) return;
    setClearing(bookingId);
    try {
      await api.delete(`/admin/payments/bookings/${bookingId}/survey-override`);
      toast.success('Override cleared — survey gate is active again.');
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear override');
    } finally {
      setClearing(null);
    }
  };

  return (
    <section data-testid="shift-survey-roster">
      <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-medium flex items-center gap-2">
        <CheckCircle2 className="w-3.5 h-3.5" />
        Post-Event Survey Status ({bookings.length} on roster)
      </div>
      <div className="border rounded divide-y">
        {bookings.map((b) => (
          <div
            key={b.booking_id}
            className="p-3 flex flex-wrap items-center justify-between gap-2 text-sm"
            data-testid={`survey-roster-row-${b.booking_id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{b.talent_label}</div>
              <div className="text-xs text-gray-500">
                {b.role_name || 'No role'} · {b.assignment_type || 'primary'}
              </div>
              {b.override_reason && (
                <div className="text-[11px] text-amber-800 mt-1 truncate" title={b.override_reason}>
                  Override reason: {b.override_reason}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <SurveyGateBadge gate={b} />
              {b.survey_status === 'missing' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => setOverrideTarget(b)}
                  data-testid={`survey-roster-override-${b.booking_id}`}
                >
                  Override
                </Button>
              )}
              {b.survey_status === 'override_applied' && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs text-gray-600 hover:text-rose-600"
                  onClick={() => clearOverride(b.booking_id)}
                  disabled={clearing === b.booking_id}
                  data-testid={`survey-roster-clear-override-${b.booking_id}`}
                >
                  {clearing === b.booking_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Clear'}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      <SurveyOverrideDialog
        open={!!overrideTarget}
        onClose={() => setOverrideTarget(null)}
        bookingId={overrideTarget?.booking_id}
        talentName={overrideTarget?.talent_label}
        onApplied={() => { setOverrideTarget(null); onRefresh?.(); }}
      />
    </section>
  );
};

const DetailModal = ({ invoiceId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!invoiceId) return;
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/admin/payments/invoice-flow/${invoiceId}`);
        if (alive) setData(res.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load invoice detail');
        if (alive) onClose();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [invoiceId, onClose, reloadTick]);

  const refresh = () => setReloadTick(t => t + 1);

  return (
    <Dialog open={!!invoiceId} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="payment-flow-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-cyan-600" />
            Payment Flow {data?.invoice?.invoice_number ? `· ${data.invoice.invoice_number}` : ''}
          </DialogTitle>
          <DialogDescription>
            Full money trail: invoice → Stripe Checkout → talent payouts.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-cyan-600" /></div>
        ) : data ? (
          <div className="space-y-5">
            {/* Invoice summary */}
            <section>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-medium">Invoice</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div><div className="text-gray-500 text-xs">Total</div><div className="font-semibold text-base">{fmt$(data.invoice.total_amount)}</div></div>
                <div><div className="text-gray-500 text-xs">Platform fee</div><div className="font-semibold text-base text-cyan-700">{fmt$(data.invoice.platform_fee_amount)}</div></div>
                <div><div className="text-gray-500 text-xs">Talent payout (owed)</div><div className="font-semibold text-base text-emerald-700">{fmt$(data.invoice.talent_payout_amount)}</div></div>
                <div><div className="text-gray-500 text-xs">Amount paid</div><div className="font-medium">{fmt$(data.invoice.amount_paid)}</div></div>
                <div><div className="text-gray-500 text-xs">Amount due</div><div className="font-medium">{fmt$(data.invoice.amount_due)}</div></div>
                <div><div className="text-gray-500 text-xs">Status</div><InvoiceChip status={data.invoice.payment_status} /></div>
                <div className="col-span-2 sm:col-span-3 text-xs">
                  <span className="text-gray-500">Client</span>{' '}
                  <span className="font-medium">
                    {data.invoice.client_company || data.invoice.client_contact || data.invoice.client_name}
                  </span>
                  {data.invoice.client_company && data.invoice.client_contact && (
                    <span className="text-gray-500"> · attn: {data.invoice.client_contact}</span>
                  )}
                  {data.invoice.venue_name && <> · <span className="text-gray-500">Shift</span> <span className="font-medium">{data.invoice.role_name} @ {data.invoice.venue_name}</span></>}
                  {data.invoice.shift_date && <> · {fmtDate(data.invoice.shift_date)}</>}
                </div>
                {data.invoice.stripe_payment_intent_id && (
                  <div className="col-span-2 sm:col-span-3 text-xs flex items-center gap-1.5 text-gray-600">
                    <CreditCard className="w-3 h-3" />
                    <span className="text-gray-500">Stripe Payment Intent / Session:</span>
                    <code className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">{data.invoice.stripe_payment_intent_id}</code>
                  </div>
                )}
                {data.invoice.paid_at && (
                  <div className="col-span-2 sm:col-span-3 text-xs text-gray-500">
                    Paid on {fmtDateTime(data.invoice.paid_at)} via {data.invoice.payment_method || 'Stripe'}
                  </div>
                )}
              </div>
            </section>

            {/* Stripe transactions */}
            <section>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-medium flex items-center gap-2">
                <CreditCard className="w-3.5 h-3.5" />
                Stripe Transactions ({data.stripe_transactions.length})
              </div>
              {data.stripe_transactions.length === 0 ? (
                <div className="text-sm text-gray-500 italic px-3 py-2 bg-gray-50 rounded">No Stripe transactions recorded for this invoice yet.</div>
              ) : (
                <div className="border rounded divide-y">
                  {data.stripe_transactions.map((t) => (
                    <div key={t.session_id} className="p-3 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`stripe-tx-${t.session_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{fmt$(t.amount)}</div>
                        <div className="text-xs text-gray-500 truncate">
                          <code className="font-mono">{t.session_id}</code>
                          {t.payment_id && <> · pi: <code className="font-mono">{t.payment_id}</code></>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <Badge className={t.payment_status === 'paid'
                          ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                          : 'bg-amber-100 text-amber-800 border-amber-300'}>
                          {t.payment_status}
                        </Badge>
                        <span className="text-gray-500">{fmtDateTime(t.processed_at || t.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Survey Gate — roster for shift */}
            {data.shift_survey_status?.length > 0 && (
              <ShiftSurveyRosterSection
                bookings={data.shift_survey_status}
                onRefresh={refresh}
              />
            )}

            {/* Payouts */}
            <section>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-medium flex items-center gap-2">
                <CircleDollarSign className="w-3.5 h-3.5" />
                Talent Payouts ({data.payouts.length})
              </div>
              {data.payouts.length === 0 ? (
                <div className="text-sm text-gray-500 italic px-3 py-2 bg-gray-50 rounded">
                  No payout records yet. Payouts are created when the client approves hours,
                  then released by an admin via the booking detail page.
                </div>
              ) : (
                <div className="space-y-3">
                  {data.payouts.map((p) => (
                    <div key={p.payout_id} className="border rounded p-3 space-y-2" data-testid={`payout-row-${p.payout_id}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <User className="w-4 h-4 text-gray-400 shrink-0" />
                          <span className="font-medium truncate">{p.talent_name}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <SurveyGateBadge gate={p.survey_gate} />
                          <PayoutChip status={p.status} />
                        </div>
                      </div>
                      {p.survey_gate?.status === 'missing' && (
                        <PayoutSurveyOverrideInline
                          bookingId={p.booking_id}
                          talentName={p.talent_name}
                          onApplied={refresh}
                        />
                      )}
                      {p.survey_gate?.status === 'override_applied' && p.survey_gate?.override_reason && (
                        <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1">
                          <strong>Override reason:</strong> {p.survey_gate.override_reason}
                        </div>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                        <div><div className="text-gray-500">Net to talent</div><div className="font-semibold text-emerald-700">{fmt$(p.amount_net)}</div></div>
                        <div><div className="text-gray-500">Gross</div><div className="font-medium">{fmt$(p.gross_amount)}</div></div>
                        <div><div className="text-gray-500">OA deduction</div><div className="font-medium">{fmt$(p.oa_deduction)}</div></div>
                        <div><div className="text-gray-500">Hours</div><div className="font-medium">{p.hours_worked != null ? p.hours_worked.toFixed(2) : '—'}</div></div>
                      </div>

                      {/* Status timeline */}
                      <div className="text-xs space-y-1 border-t pt-2 mt-1 text-gray-600">
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-500">Created:</span>
                          <span>{fmtDateTime(p.created_at)}</span>
                        </div>
                        {p.processed_at && (
                          <div className="flex justify-between gap-2">
                            <span className="text-gray-500">Stripe Transfer initiated:</span>
                            <span>{fmtDateTime(p.processed_at)}</span>
                          </div>
                        )}
                        {p.completed_at && (
                          <div className="flex justify-between gap-2 text-emerald-700">
                            <span>Completed (cleared to talent):</span>
                            <span>{fmtDateTime(p.completed_at)}</span>
                          </div>
                        )}
                        {p.stripe_transfer_id && (
                          <div className="flex justify-between gap-2 items-start">
                            <span className="text-gray-500">Transfer ID:</span>
                            <code className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-[11px] break-all">{p.stripe_transfer_id}</code>
                          </div>
                        )}
                        {p.failed_reason && (
                          <div className="text-rose-700 flex items-start gap-1.5 bg-rose-50 border border-rose-200 rounded px-2 py-1.5 mt-1">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            <span className="break-words"><strong>Failure:</strong> {p.failed_reason}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        ) : (
          <div className="text-sm text-gray-500 italic">Failed to load detail.</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────

const AdminPaymentFlow = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [payoutFilter, setPayoutFilter] = useState('all');
  const [detailId, setDetailId] = useState(null);
  const [stripeMode, setStripeMode] = useState(null);
  const [showLegend, setShowLegend] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const res = await api.get('/admin/payments/invoice-flow?limit=200');
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load payment flow');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // Fire-and-forget — failing silently is fine; the badge just hides.
    api.get('/admin/payments/stripe-mode')
      .then((r) => setStripeMode(r.data?.mode || null))
      .catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!data?.invoices) return [];
    let rows = data.invoices;
    if (paymentFilter !== 'all') rows = rows.filter(r => r.invoice_payment_status === paymentFilter);
    if (payoutFilter !== 'all')   rows = rows.filter(r => r.payout_status === payoutFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(r =>
        (r.invoice_number || '').toLowerCase().includes(q) ||
        (r.client?.name   || '').toLowerCase().includes(q) ||
        (r.client?.email  || '').toLowerCase().includes(q) ||
        (r.shift?.venue_name || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [data, paymentFilter, payoutFilter, search]);

  const totals = data?.totals;
  const payoutTotals = totals?.payouts;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-payment-flow">
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-3 flex-wrap min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')} data-testid="back-btn" className="shrink-0">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Button>
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2 min-w-0">
              <DollarSign className="w-5 h-5 text-emerald-600 shrink-0" />
              <span className="truncate">Payment Flow</span>
            </h1>
            {stripeMode && (
              <Badge
                className={`whitespace-nowrap shrink-0 ${
                  stripeMode === 'live'
                    ? 'bg-rose-600 hover:bg-rose-600 text-white border-rose-700 font-semibold tracking-wide'
                    : stripeMode === 'test'
                    ? 'bg-emerald-100 hover:bg-emerald-100 text-emerald-800 border-emerald-300 font-semibold tracking-wide'
                    : 'bg-amber-100 hover:bg-amber-100 text-amber-800 border-amber-300 font-semibold tracking-wide'
                }`}
                data-testid={`stripe-mode-badge-${stripeMode}`}
                title={
                  stripeMode === 'live'
                    ? 'Stripe is in LIVE mode — real money is being moved.'
                    : stripeMode === 'test'
                    ? 'Stripe is in TEST mode — no real money is being moved.'
                    : 'Stripe key is missing or unrecognized.'
                }
              >
                Stripe: {stripeMode.toUpperCase()}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <Button variant="outline" size="sm" onClick={load} disabled={refreshing} data-testid="refresh-btn" className="shrink-0">
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowLegend(true)} data-testid="status-legend-btn" className="shrink-0">
              <Info className="w-4 h-4 mr-1" /> Legend
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-5">
        {/* TOTALS */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <TotalsCard
            label="Revenue collected"
            value={fmt$(totals?.revenue_collected)}
            sub={`${totals?.paid_count || 0} paid invoice${totals?.paid_count === 1 ? '' : 's'}`}
            color="emerald"
            icon={CheckCircle2}
          />
          <TotalsCard
            label="Outstanding"
            value={fmt$(totals?.revenue_outstanding)}
            sub={`${totals?.unpaid_count || 0} unpaid`}
            color="amber"
            icon={Clock}
          />
          <TotalsCard
            label="Platform retained"
            value={fmt$(totals?.platform_retained)}
            sub="Fees from paid invoices"
            color="cyan"
            icon={DollarSign}
          />
          <TotalsCard
            label="Talent owed"
            value={fmt$(totals?.talent_owed)}
            sub="From paid invoices"
            color="purple"
            icon={CircleDollarSign}
          />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <TotalsCard
            label="Payouts pending"
            value={fmt$(payoutTotals?.pending_total)}
            sub={`${payoutTotals?.pending_count || 0} record${payoutTotals?.pending_count === 1 ? '' : 's'}`}
            color="amber"
            icon={Clock}
          />
          <TotalsCard
            label="Payouts processing"
            value={fmt$(payoutTotals?.processing_total)}
            sub={`${payoutTotals?.processing_count || 0} record${payoutTotals?.processing_count === 1 ? '' : 's'}`}
            color="cyan"
            icon={Loader2}
          />
          <TotalsCard
            label="Payouts released"
            value={fmt$(payoutTotals?.released_total)}
            sub={`${payoutTotals?.released_count || 0} record${payoutTotals?.released_count === 1 ? '' : 's'}`}
            color="emerald"
            icon={CheckCircle2}
          />
          <TotalsCard
            label="Payouts failed"
            value={fmt$(payoutTotals?.failed_total)}
            sub={`${payoutTotals?.failed_count || 0} record${payoutTotals?.failed_count === 1 ? '' : 's'} · needs retry`}
            color="rose"
            icon={AlertTriangle}
          />
        </div>

        {/* FILTERS */}
        <Card>
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search invoice #, client, venue…"
                className="pl-8 h-9 text-sm"
                data-testid="search-input"
              />
            </div>
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="h-9 px-2 text-sm border border-gray-200 rounded bg-white"
              data-testid="payment-filter"
            >
              <option value="all">All invoice statuses</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
              <option value="partial">Partial</option>
            </select>
            <select
              value={payoutFilter}
              onChange={(e) => setPayoutFilter(e.target.value)}
              className="h-9 px-2 text-sm border border-gray-200 rounded bg-white"
              data-testid="payout-filter"
            >
              <option value="all">All payout statuses</option>
              <option value="none">No payout yet</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="released">Released</option>
              <option value="failed">Failed</option>
              <option value="mixed">Partial / mixed</option>
            </select>
            <div className="text-xs text-gray-500 ml-auto">
              {filtered.length} of {data?.invoices?.length || 0} invoice{filtered.length === 1 ? '' : 's'}
            </div>
          </CardContent>
        </Card>

        {/* TABLE */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="payment-flow-table">
              <thead className="bg-gray-50 border-b text-xs uppercase tracking-wide text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Invoice</th>
                  <th className="text-left px-3 py-2 font-semibold">Client / Shift</th>
                  <th className="text-right px-3 py-2 font-semibold">Total</th>
                  <th className="text-right px-3 py-2 font-semibold">Platform Fee</th>
                  <th className="text-right px-3 py-2 font-semibold">Talent Payout</th>
                  <th className="text-left px-3 py-2 font-semibold">Invoice</th>
                  <th className="text-left px-3 py-2 font-semibold">Payout</th>
                  <th className="text-left px-3 py-2 font-semibold">Bookings</th>
                  <th className="text-right px-3 py-2 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan="9" className="text-center py-8 text-gray-500 italic">No invoices match those filters.</td></tr>
                )}
                {filtered.map((row) => (
                  <tr
                    key={row.invoice_id}
                    className="border-b hover:bg-gray-50 cursor-pointer"
                    onClick={() => setDetailId(row.invoice_id)}
                    data-testid={`payment-flow-row-${row.invoice_id}`}
                  >
                    <td className="px-3 py-2.5">
                      <div className="font-mono text-xs font-medium">{row.invoice_number}</div>
                      <div className="text-[11px] text-gray-500">{fmtDate(row.invoice_date)}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-gray-900 truncate max-w-[200px]" title={row.client?.company_name || row.client?.contact_name || row.client?.email || ''}>
                        {row.client?.company_name || row.client?.contact_name || row.client?.email || '—'}
                      </div>
                      {row.client?.company_name && row.client?.contact_name && (
                        <div className="text-[11px] text-gray-500 truncate max-w-[200px]">
                          {row.client.contact_name}
                        </div>
                      )}
                      {row.shift?.venue_name && (
                        <div className="text-[11px] text-gray-500 truncate max-w-[200px]">
                          {row.shift.role_name || ''} · {row.shift.venue_name}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{fmt$(row.amounts.total_amount)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-cyan-700">{fmt$(row.amounts.platform_fee_amount)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700">{fmt$(row.amounts.talent_payout_amount)}</td>
                    <td className="px-3 py-2.5"><InvoiceChip status={row.invoice_payment_status} /></td>
                    <td className="px-3 py-2.5"><PayoutChip status={row.payout_status} /></td>
                    <td className="px-3 py-2.5 text-xs text-gray-700">
                      {row.payouts.length > 0
                        ? `${row.payouts.length} talent`
                        : <span className="text-gray-400 italic">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Button variant="ghost" size="sm" className="h-7" onClick={(e) => { e.stopPropagation(); setDetailId(row.invoice_id); }}>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </main>

      {detailId && <DetailModal invoiceId={detailId} onClose={() => setDetailId(null)} />}

      <Dialog open={showLegend} onOpenChange={setShowLegend}>
        <DialogContent className="max-w-lg" data-testid="status-legend-dialog">
          <DialogHeader>
            <DialogTitle>Status Color Legend</DialogTitle>
            <DialogDescription>
              The same colors are used across the dashboard, attendance, and shift views.
            </DialogDescription>
          </DialogHeader>
          <StatusLegend />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPaymentFlow;
